import { overlayAlpha } from './fades';
import type { OverlayTiming } from './project';

/*
 * Laying the pictures of text/logos/pictures (src/video/overlays.ts) and the fades over video frames.
 * No Fabric here: the export (src/video/exporter.ts, loaded only when writing a video) uses it too.
 */

export interface Layer extends OverlayTiming {
  /** where the picture goes, in design px (the work's frame, e.g. 1080 × 1920) */
  x: number; y: number; w: number; h: number;
  img: HTMLCanvasElement;
}

/** how the work's frame maps onto a target frame: same shape = scale only; another shape = fill it, centred (like the clips) */
export function coverMap(from: { w: number; h: number }, to: { w: number; h: number }) {
  const s = Math.max(to.w / from.w, to.h / from.h);
  return { s, ox: (to.w - from.w * s) / 2, oy: (to.h - from.h * s) / 2 };
}

/** layers that do not fit wholly inside a target frame of another shape (e.g. a 9:16 work written as 1:1) */
export function layersCut(layers: Layer[], from: { w: number; h: number }, to: { w: number; h: number }): number {
  const m = coverMap(from, to);
  return layers.filter(l => {
    const x = m.ox + l.x * m.s, y = m.oy + l.y * m.s;
    return x < -0.5 || y < -0.5 || x + l.w * m.s > to.w + 0.5 || y + l.h * m.s > to.h + 0.5;
  }).length;
}

/** lay the layers that show at timeline time `t` over a frame */
export function drawLayers(g: CanvasRenderingContext2D, layers: Layer[], t: number, m: { s: number; ox: number; oy: number }) {
  for (const l of layers) {
    const a = overlayAlpha(l, t);
    if (a <= 0) continue;
    g.globalAlpha = a;
    g.drawImage(l.img, m.ox + l.x * m.s, m.oy + l.y * m.s, l.w * m.s, l.h * m.s);
  }
  g.globalAlpha = 1;
}

/** black over a frame (a fade), `a` 0..1 */
export function drawBlack(g: CanvasRenderingContext2D, a: number, w: number, h: number) {
  if (a <= 0) return;
  g.globalAlpha = Math.min(1, a);
  g.fillStyle = '#000';
  g.fillRect(0, 0, w, h);
  g.globalAlpha = 1;
}
